import { useCallback, useEffect, useRef, useState } from "react";
import { GripVertical } from "lucide-react";

interface PanelResizeHandleProps {
  onResize: (leftRatio: number | ((prev: number) => number)) => void;
}

export function PanelResizeHandle({ onResize }: PanelResizeHandleProps) {
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const parent = containerRef.current?.parentElement;
      if (!parent) return;

      const parentRect = parent.getBoundingClientRect();
      const ratio = (e.clientX - parentRect.left) / parentRect.width;
      const clamped = Math.max(0.25, Math.min(0.75, ratio));
      onResize(clamped);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isDragging, onResize]);

  return (
    <div
      ref={containerRef}
      className="group relative flex w-3 cursor-col-resize items-center justify-center border-x border-border bg-muted/40 hover:bg-accent"
      onMouseDown={handleMouseDown}
      role="separator"
      aria-orientation="vertical"
      tabIndex={0}
      aria-label="패널 크기 조절"
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") onResize((prev) => Math.max(0.25, prev - 0.02));
        if (e.key === "ArrowRight") onResize((prev) => Math.min(0.75, prev + 0.02));
      }}
    >
      <GripVertical className="h-4 w-4 text-muted-foreground/50 transition-opacity group-hover:opacity-100" />
      {isDragging && <div className="fixed inset-0 z-50 cursor-col-resize" />}
    </div>
  );
}
